import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Loader2, RefreshCw, Smartphone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  connectWaWeb, disconnectWaWeb, getNotificationProviders, getWaWebQr, getWaWebStatus, setNotificationPreference,
} from "@/lib/api";
import { describeOverview, providerLabel, secondsLeft } from "@/lib/waWeb";

const POLL_MS = 3000;
const errorText = (error) => (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : "Permintaan belum berhasil. Coba lagi.");

// `renderQr` is injected: this build ships no QR encoder, so a QR is never drawn from a guess. Without it the card says so.
export default function NotificationSettings({ renderQr = null }) {
  const [overview, setOverview] = useState({ status: "loading", data: null });
  const [providers, setProviders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [qr, setQr] = useState(null);
  const [now, setNow] = useState(Date.now());
  const alive = useRef(true);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const data = await getWaWebStatus();
      if (alive.current) setOverview({ status: "ready", data });
      return data;
    } catch (error) {
      if (alive.current) setOverview({ status: "error", data: null });
      return null;
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    refresh();
    getNotificationProviders().then((p) => { if (alive.current) setProviders(p); }).catch(() => {});
    return () => { alive.current = false; };
  }, [refresh]);

  const view = describeOverview(overview.status === "ready" ? overview.data : null);

  useEffect(() => {
    if (!view.shouldPoll) return undefined;
    const timer = setInterval(() => { setNow(Date.now()); refresh(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [view.shouldPoll, refresh]);

  useEffect(() => {
    if (!view.showQrArea) { setQr(null); return undefined; }
    let cancelled = false;
    getWaWebQr().then((data) => { if (!cancelled) setQr(data); }).catch(() => { if (!cancelled) setQr(null); });
    return () => { cancelled = true; };
  }, [view.showQrArea, overview.data?.session?.qr_expires_at]);

  // One mutation at a time: a double click can never start two connections or two disconnects.
  const act = async (fn, okText) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setMessage("");
    try { const data = await fn(); if (alive.current) { setOverview({ status: "ready", data }); setMessage(okText || ""); } }
    catch (error) { if (alive.current) setMessage(errorText(error)); await refresh(); }
    finally { inFlight.current = false; if (alive.current) setBusy(false); }
  };
  const disconnect = () => { if (window.confirm("Putuskan koneksi WhatsApp Web? Data sesi akan dihapus dan Anda perlu scan QR lagi.")) act(disconnectWaWeb, "Koneksi diputuskan."); };
  const choose = (provider) => act(async () => { const p = await setNotificationPreference(provider); if (alive.current) setProviders(p); return (await getWaWebStatus()); }, "Pilihan disimpan.");

  const left = qr?.expires_at ? secondsLeft(qr.expires_at, now) : 0;
  const cloud = providers?.providers?.whatsapp_cloud_api;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="notification-settings">
      <div className="flex items-center gap-2"><Bell className="h-6 w-6 text-slate-700" /><h1 className="text-xl font-semibold text-slate-900">Notifikasi</h1></div>
      <p className="mt-1 text-sm text-slate-600" data-testid="notif-intro">
        Pilih cara menerima notifikasi. Kedua metode dikelola terpisah. Saat ini belum ada pesan yang dikirim ke pengguna.
      </p>
      {providers && (
        <p className="mt-2 text-xs text-slate-500" data-testid="notif-selected">
          Pilihan Anda: {providers.selected ? providerLabel(providers.selected) : "belum dipilih"}. Pengiriman notifikasi pengguna: {providers.delivery_enabled ? "aktif" : "belum aktif"}.
        </p>
      )}

      <section className="mt-5 rounded-lg border border-slate-200 p-4" data-testid="wa-web-card">
        <div className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-slate-700" /><h2 className="font-medium text-slate-900">{providerLabel("whatsapp_web_qr")}</h2></div>
        <p className="mt-1 text-xs text-amber-700" data-testid="wa-web-risk">
          Metode tidak resmi: akun WhatsApp Anda dapat dibatasi oleh WhatsApp. Gunakan nomor yang Anda miliki sendiri.
        </p>

        {overview.status === "loading" && <div className="mt-3 flex items-center gap-2 text-sm text-slate-500" data-testid="wa-web-loading"><Loader2 className="h-4 w-4 animate-spin" /> Memuat status…</div>}
        {overview.status === "error" && <p className="mt-3 text-sm text-amber-800" role="alert" data-testid="wa-web-error">Status tidak dapat dimuat. Tidak ada koneksi yang dianggap aktif.</p>}

        {overview.status === "ready" && (
          <div className="mt-3" aria-live="polite">
            <div className="text-sm"><span className="font-medium">Status:</span> <span data-testid="wa-web-status" data-state={view.state || "unavailable"}>{view.label}</span></div>
            {view.text && <p className="mt-1 text-sm text-slate-600" data-testid="wa-web-text">{view.text}</p>}
            {view.reason && <p className="mt-1 text-sm text-slate-600" data-testid="wa-web-reason">{view.reason}</p>}

            {view.showQrArea && (
              <div className="mt-3 rounded border border-slate-200 p-3" data-testid="wa-web-qr-area">
                {qr && renderQr ? <div data-testid="wa-web-qr">{renderQr(qr.qr)}</div>
                  : <p className="text-sm text-slate-600" data-testid="wa-web-qr-unavailable">{qr ? "Penampil QR belum tersedia di versi aplikasi ini." : "Memuat QR…"}</p>}
                {qr && <p className="mt-2 text-xs text-slate-500" data-testid="wa-web-qr-timer">{left > 0 ? `QR berlaku ${left} detik lagi.` : "QR kedaluwarsa. Minta QR baru."}</p>}
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <Button disabled={busy || !view.canConnect} onClick={() => act(connectWaWeb)} data-testid="wa-web-connect">
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}{view.state === "disconnected" || view.state === "error" ? "Hubungkan ulang" : "Hubungkan"}
              </Button>
              <Button variant="outline" disabled={busy} onClick={() => refresh()} data-testid="wa-web-refresh"><RefreshCw className="mr-1 h-4 w-4" /> Periksa status</Button>
              <Button variant="outline" disabled={busy || !view.canDisconnect} onClick={disconnect} data-testid="wa-web-disconnect">Putuskan koneksi</Button>
              <Button variant="outline" disabled={busy || !view.available} onClick={() => choose("whatsapp_web_qr")} data-testid="wa-web-choose">Pakai metode ini</Button>
            </div>
          </div>
        )}
      </section>

      <section className="mt-4 rounded-lg border border-slate-200 p-4" data-testid="wa-cloud-card">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-slate-700" /><h2 className="font-medium text-slate-900">{providerLabel("whatsapp_cloud_api")}</h2></div>
        <p className="mt-1 text-sm text-slate-600" data-testid="wa-cloud-note">{cloud?.note || "Metode resmi Meta. Dikelola admin dan terpisah dari WhatsApp Web."}</p>
        <div className="mt-3"><Button variant="outline" disabled={busy} onClick={() => choose("whatsapp_cloud_api")} data-testid="wa-cloud-choose">Pakai metode ini</Button></div>
      </section>

      {message && <p className="mt-3 text-sm text-slate-700" role="status" data-testid="notif-message">{message}</p>}
    </div>
  );
}
